/**
 * 圖片附件 Lightbox(REQ-20260421001;縮放功能 2026-05-13)。
 *
 * - 支援同訪談內多張圖片 prev/next
 * - 鍵盤:← / → 切換;Esc 關閉;+/-/0 縮放
 * - 滾輪以游標位置為錨點縮放;拖曳平移;雙擊切換 1x↔2x
 * - ↺ / ↻ 以 90 度為單位旋轉(REQ-20260909001);呼叫端有給 `onSaveRotation` 時
 *   多一顆「存」把角度回存到資料庫,下次開啟自動套用(REQ-20260909004)。
 *   ⚠️ **回存的只是「看的時候要轉幾度」** —— MinIO 上的原圖與它的 EXIF 一個 byte
 *   都沒動(REQ-20260909001 §2.2 否決「轉檔重寫原圖」的理由不變:附件是單據憑證,
 *   覆蓋原圖是不可逆的)。沒有 `onSaveRotation` 的呼叫端仍然是純檢視旋轉
 * - signed URL 按需取得 + 快取(避免 5 分鐘過期,也避免一次預取全部);
 *   取號方式可由呼叫端用 `resolveUrl` 覆寫(REQ-20260901011)
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchDownloadUrl } from './api';
import type { InterviewOwnerType } from '../interviews/api';

export interface LightboxImage {
  uuid: string;
  originalName: string;
  /** 該圖所屬訪談;跨訪談檢視(圖片牆)才會帶,單一訪談內的附件列表不需要 */
  interviewId?: number;
  /**
   * 已存的檢視角度(REQ-20260909004)。開啟 / 換圖 / 按 1:1 時回到這個角度,
   * 沒帶就是 0(沒轉過)。**檔案本身的方向與此無關**。
   */
  rotation?: number;
}

interface Props {
  images: LightboxImage[];
  initialIndex: number;
  onClose: () => void;
  /** owner 類型;決定 fetchDownloadUrl 走 /attachments 還是 /supplier-attachments。預設 'customer' */
  ownerType?: InterviewOwnerType;
  /**
   * 自備取號方式(REQ-20260901011)。有傳時就不呼叫 `fetchDownloadUrl` ——
   * 進貨單附件的 signed URL 在另一組端點,這樣就不必為了它改動訪談 / 客戶 /
   * 廠商附件共用的那條路。沒傳時行為與原本完全一樣。
   */
  resolveUrl?: (image: LightboxImage) => Promise<string>;
  /** 有傳且該圖有 interviewId 時,工具列多一顆「看訪談」按鈕(REQ-20260819012) */
  onJumpToInterview?: (interviewId: number) => void;
  /**
   * 有傳時工具列多一顆「存」,把目前角度回存(REQ-20260909004)。
   * 沒傳(或呼叫端判定使用者只能唯讀)就完全不渲染那顆鈕,行為與本次改動前相同。
   * 回存成功後由呼叫端負責重取,新的 `image.rotation` 會回流成「已存角度」。
   */
  onSaveRotation?: (image: LightboxImage, rotation: number) => Promise<void>;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.25;

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

export function ImageLightbox({
  images,
  initialIndex,
  onClose,
  ownerType = 'customer',
  resolveUrl,
  onJumpToInterview,
  onSaveRotation,
}: Props): JSX.Element | null {
  const [idx, setIdx] = useState(initialIndex);
  const [urlCache, setUrlCache] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const [scale, setScale] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  /**
   * 檢視用的旋轉角度,只會是 0 / 90 / 180 / 270。
   * 初始值直接取該圖已存的角度(REQ-20260909004)—— 用 lazy initializer 而不是
   * 等下面那個 effect 補,不然第一幀會先畫 0 度再跳到正確角度。
   */
  const [rotation, setRotation] = useState(() => images[initialIndex]?.rotation ?? 0);
  const [isDragging, setIsDragging] = useState(false);
  const [savingRotation, setSavingRotation] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // 把最新 state 鏡像到 ref,給原生 wheel listener 在 closure 外讀取
  const stateRef = useRef({ scale, offsetX, offsetY });
  stateRef.current = { scale, offsetX, offsetY };

  // resolveUrl 同樣走 ref:呼叫端若傳未 memo 的箭頭函式,把它放進下面那個
  // signed URL effect 的依賴陣列會變成每次 render 都重取 → 無限迴圈。
  // effect 只讀 ref 的最新值,依賴陣列維持 [current, urlCache]。
  const resolveUrlRef = useRef(resolveUrl);
  resolveUrlRef.current = resolveUrl;

  const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const imgRef = useRef<HTMLImageElement | null>(null);

  const total = images.length;
  const current = images[idx];
  const currentUrl = current ? urlCache[current.uuid] : undefined;

  const goPrev = useCallback(() => {
    setIdx((i) => (i > 0 ? i - 1 : i));
  }, []);
  const goNext = useCallback(() => {
    setIdx((i) => (i < total - 1 ? i + 1 : i));
  }, [total]);

  /**
   * 該圖已存的角度(REQ-20260909004);沒存過就是 0。
   * 走 ref 是為了讓 `resetView` 的依賴陣列維持空的 —— 否則回存成功、呼叫端重取後
   * `savedRotation` 一變,`resetView` 換身分,底下那個 `[idx, resetView]` 的 effect
   * 就會跟著重跑,把使用者剛才的縮放與平移一起清掉。
   */
  const savedRotation = current?.rotation ?? 0;
  const savedRotationRef = useRef(savedRotation);
  savedRotationRef.current = savedRotation;

  /**
   * 回到原狀:縮放與平移歸零,角度回到**該圖已存的角度**
   * (1:1 鈕 / 鍵盤 0 / 雙擊 / 換圖都走這裡)。
   * 歸零成 0 度是不對的 —— 已經存正的照片按一次 1:1 又會躺回去。
   */
  const resetView = useCallback(() => {
    setScale(1);
    setOffsetX(0);
    setOffsetY(0);
    setRotation(savedRotationRef.current);
  }, []);

  const rotateLeft = useCallback(() => {
    setRotation((r) => (r - 90 + 360) % 360);
  }, []);

  const rotateRight = useCallback(() => {
    setRotation((r) => (r + 90 + 360) % 360);
  }, []);

  const zoomIn = useCallback(() => {
    setScale((s) => clamp(s * ZOOM_STEP, MIN_SCALE, MAX_SCALE));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => {
      const ns = clamp(prev / ZOOM_STEP, MIN_SCALE, MAX_SCALE);
      if (ns === 1) {
        setOffsetX(0);
        setOffsetY(0);
      }
      return ns;
    });
  }, []);

  // 換圖時自動重置縮放/平移,並把角度換成下一張自己已存的角度,
  // 避免下一張帶著上一張的 offset 或角度;上一張的存檔錯誤也一起清掉
  useEffect(() => {
    resetView();
    setSaveError(null);
  }, [idx, resetView]);

  // signed URL 載入
  useEffect(() => {
    if (!current) return;
    if (urlCache[current.uuid]) return;
    let cancelled = false;
    setError(null);
    const resolve = resolveUrlRef.current;
    (resolve ? resolve(current) : fetchDownloadUrl(current.uuid, ownerType))
      .then((url) => {
        if (cancelled) return;
        setUrlCache((prev) => ({ ...prev, [current.uuid]: url }));
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : '載入失敗');
      });
    return () => {
      cancelled = true;
    };
  }, [current, urlCache]);

  // 鍵盤:← / → / Esc / + / - / 0
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === '+' || e.key === '=') zoomIn();
      else if (e.key === '-' || e.key === '_') zoomOut();
      else if (e.key === '0') resetView();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, goPrev, goNext, zoomIn, zoomOut, resetView]);

  // 拖曳平移(scale > 1 才作用):mousedown 在 img 上開始,mousemove/mouseup 用 window 監聽
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent): void => {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      setOffsetX(dragStartRef.current.ox + dx);
      setOffsetY(dragStartRef.current.oy + dy);
    };
    const onUp = (): void => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  // 滾輪縮放:React 的 onWheel 預設 passive=true,e.preventDefault 不會生效;
  // 改用原生 listener with passive:false 才能阻擋頁面捲動。
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return undefined;
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const { scale: s, offsetX: ox, offsetY: oy } = stateRef.current;
      const factor = e.deltaY < 0 ? ZOOM_STEP : 1 / ZOOM_STEP;
      const ns = clamp(s * factor, MIN_SCALE, MAX_SCALE);
      if (ns === s) return;
      // 以游標為錨點:游標下的影像點保持不動
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - (rect.left + rect.width / 2);
      const cy = e.clientY - (rect.top + rect.height / 2);
      const ratio = ns / s;
      setScale(ns);
      setOffsetX(ox - cx * (ratio - 1));
      setOffsetY(oy - cy * (ratio - 1));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [currentUrl]);

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>): void => {
    if (stateRef.current.scale <= 1) return;
    e.preventDefault();
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: stateRef.current.offsetX,
      oy: stateRef.current.offsetY,
    };
  };

  const handleDoubleClick = (): void => {
    if (stateRef.current.scale > 1) {
      resetView();
    } else {
      setScale(2);
    }
  };

  /**
   * 回存目前角度(REQ-20260909004)。失敗訊息就地顯示 —— 不用 `alert()`,
   * 也不塞進上面那個 `error`(那格是「圖載不出來」用的,一有值圖片就會被換掉,
   * 而存檔失敗時使用者還在看這張圖)。
   */
  const handleSaveRotation = async (): Promise<void> => {
    if (!onSaveRotation || !current) return;
    setSaveError(null);
    setSavingRotation(true);
    try {
      await onSaveRotation(current, rotation);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : '儲存失敗');
    } finally {
      setSavingRotation(false);
    }
  };

  if (!current) return null;

  const jumpInterviewId = current.interviewId;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="圖片預覽"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center"
      data-testid="image-lightbox"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-4 right-4 z-10 text-white text-2xl w-10 h-10 rounded hover:bg-white/10"
        data-testid="lightbox-close"
        aria-label="關閉"
      >
        ×
      </button>

      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          goPrev();
        }}
        disabled={idx === 0}
        className="absolute left-4 z-10 text-white text-3xl w-12 h-12 rounded hover:bg-white/10 disabled:opacity-30"
        data-testid="lightbox-prev"
        aria-label="上一張"
      >
        ‹
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          goNext();
        }}
        disabled={idx === total - 1}
        className="absolute right-4 z-10 text-white text-3xl w-12 h-12 rounded hover:bg-white/10 disabled:opacity-30"
        data-testid="lightbox-next"
        aria-label="下一張"
      >
        ›
      </button>

      <div
        onClick={(e) => e.stopPropagation()}
        className="max-w-[90vw] max-h-[90vh] flex flex-col items-center gap-3"
      >
        {error ? (
          <div role="alert" className="text-red-300 text-sm">
            載入失敗:{error}
          </div>
        ) : !currentUrl ? (
          <div className="text-white text-sm">載入中…</div>
        ) : (
          <img
            ref={imgRef}
            src={currentUrl}
            alt={current.originalName}
            draggable={false}
            className="max-w-[90vw] max-h-[85vh] object-contain select-none"
            data-testid="lightbox-image"
            onMouseDown={handleMouseDown}
            onDoubleClick={handleDoubleClick}
            style={{
              // translate 在最外層,拖曳平移才維持螢幕座標(方向與旋轉角度無關);
              // rotate 放在 scale 之前,縮放才是沿著螢幕的水平/垂直軸。
              transform: `translate(${offsetX}px, ${offsetY}px) rotate(${rotation}deg) scale(${scale})`,
              // 轉 90/270 度時長短邊互換:版面框沒變,但畫出來的外框是它的轉置,
              // 沿用 class 的 max-w-[90vw] / max-h-[85vh] 會讓橫幅照片轉正後上下被切掉。
              // 這裡把兩個上限對調(inline style 蓋得過 Tailwind 的 class)。
              ...(rotation % 180 !== 0 ? { maxWidth: '85vh', maxHeight: '90vw' } : {}),
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
              transition: isDragging ? 'none' : 'transform 0.15s ease-out',
            }}
          />
        )}

        {/* 縮放 / 旋轉工具列 + 檔名 + 序號 */}
        <div className="flex items-center gap-3 text-white text-xs flex-wrap justify-center">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              className="w-7 h-7 rounded border border-white/30 hover:bg-white/10 disabled:opacity-30 leading-none text-base"
              aria-label="縮小"
              data-testid="lightbox-zoom-out"
            >
              −
            </button>
            <span
              className="tabular-nums w-12 text-center"
              data-testid="lightbox-zoom-level"
            >
              {Math.round(scale * 100)}%
            </span>
            <button
              type="button"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              className="w-7 h-7 rounded border border-white/30 hover:bg-white/10 disabled:opacity-30 leading-none text-base"
              aria-label="放大"
              data-testid="lightbox-zoom-in"
            >
              +
            </button>
            <button
              type="button"
              onClick={resetView}
              // 「已經是原狀」= 沒縮放、沒平移,而且角度就是該圖**已存的**角度
              // (REQ-20260909004;原本比的是 0 度)。已存成 90 度的照片一開啟就在
              // 90 度,這裡若還比 0,1:1 會一直是 enabled 卻按了沒反應 ——
              // 它要回的本來就是那個已存角度。
              // aria-label 維持「重置縮放與旋轉」:回的是已存角度,不是 0 度。
              disabled={
                scale === 1 &&
                offsetX === 0 &&
                offsetY === 0 &&
                rotation === savedRotation
              }
              className="ml-1 px-2 h-7 rounded border border-white/30 hover:bg-white/10 disabled:opacity-30 leading-none"
              aria-label="重置縮放與旋轉"
              data-testid="lightbox-zoom-reset"
            >
              1:1
            </button>
            <button
              type="button"
              onClick={rotateLeft}
              className="w-7 h-7 rounded border border-white/30 hover:bg-white/10 leading-none text-base"
              aria-label="向左旋轉"
              data-testid="lightbox-rotate-left"
            >
              ↺
            </button>
            <button
              type="button"
              onClick={rotateRight}
              className="w-7 h-7 rounded border border-white/30 hover:bg-white/10 leading-none text-base"
              aria-label="向右旋轉"
              data-testid="lightbox-rotate-right"
            >
              ↻
            </button>
            {/*
              回存角度(REQ-20260909004)。**一定要 type="button"** —— 這顆元件會被
              DocumentAttachmentCard 掛進進貨單的 <form> 裡,不寫 type 預設是 submit,
              按下去會送出整張單(REQ-20260901011 在縮圖鈕上踩過同一個坑)。
              呼叫端沒給 onSaveRotation(例如唯讀帳號)時整顆不渲染。
            */}
            {onSaveRotation ? (
              <button
                type="button"
                onClick={() => void handleSaveRotation()}
                disabled={savingRotation || rotation === savedRotation}
                className="w-7 h-7 rounded border border-white/30 hover:bg-white/10 disabled:opacity-30 leading-none text-base"
                aria-label="儲存旋轉角度"
                data-testid="lightbox-save-rotation"
              >
                存
              </button>
            ) : null}
          </div>
          <span className="text-white/40" aria-hidden="true">·</span>
          <span>
            {current.originalName} ({idx + 1} / {total})
          </span>
          {onJumpToInterview && jumpInterviewId != null ? (
            <>
              <span className="text-white/40" aria-hidden="true">·</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onJumpToInterview(jumpInterviewId);
                }}
                className="px-2 h-7 rounded border border-white/30 hover:bg-white/10 leading-none"
                data-testid="lightbox-goto-interview"
              >
                看訪談
              </button>
            </>
          ) : null}
        </div>

        {saveError ? (
          <div role="alert" className="text-red-300 text-sm" data-testid="lightbox-save-rotation-error">
            儲存角度失敗:{saveError}
          </div>
        ) : null}
      </div>
    </div>
  );
}
